import { useEffect, useMemo, useState } from "react"
import i18n from "../../../../i18n"
import ProductOptions from "./ProductOptions"

const attributeValueLabel = (value) => (
    value?.value || value?.translated__value || value?.code || ''
)

const attributeName = (value) => (
    value?.attribute_object?.name || value?.attribute || i18n.t('Variant')
)

const ProductReservationCard = ({product, variants = [], cart, setCart, overviewType = 'list'}) => {
    const variantList = variants.length ? variants : [product]
    const [selectedVariantId, setSelectedVariantId] = useState(variantList[0]?.id)
    const selectedProduct = variantList.find((item) => item.id === selectedVariantId) || variantList[0] || product
    const [quantity, setQuantity] = useState(0)
    const [people, setPeople] = useState(1)
    const [message, setMessage] = useState(null)
    const [selectedOptions, setSelectedOptions] = useState({})
    const [selectedAttributes, setSelectedAttributes] = useState({})
    const cartItemQ = cart[selectedProduct.id]?.quantity

    const attributeChoices = useMemo(() => {
        const byAttribute = {}
        variantList.forEach((variant) => {
            (variant.attribute_values || []).forEach((value) => {
                const attrId = value.attribute_object?.id || value.attribute || attributeName(value)
                if (!byAttribute[attrId]) {
                    byAttribute[attrId] = {
                        id: attrId,
                        name: attributeName(value),
                        values: [],
                    }
                }
                if (!byAttribute[attrId].values.some((item) => item.id === value.id)) {
                    byAttribute[attrId].values.push({
                        id: value.id,
                        label: attributeValueLabel(value),
                    })
                }
            })
        })
        return Object.values(byAttribute).filter((attr) => attr.values.length > 1)
    }, [variantList])

    useEffect(() => {
        setQuantity(cartItemQ || 0)
    }, [cartItemQ])

    useEffect(() => {
        if (!attributeChoices.length) {
            return
        }
        const match = variantList.find((variant) => (
            attributeChoices.every((attr) => {
                const selected = selectedAttributes[attr.id]
                if (!selected) {
                    return true
                }
                return (variant.attribute_values || []).some((value) => value.id === selected)
            })
        ))
        if (match && match.id !== selectedVariantId) {
            setSelectedVariantId(match.id)
            setSelectedOptions({})
        }
    }, [attributeChoices, selectedAttributes, selectedVariantId, variantList])

    useEffect(() => {
        if (cart[selectedProduct.id]?.options) {
            setSelectedOptions(cart[selectedProduct.id].options)
        }
        if (cart[selectedProduct.id]?.people) {
            setPeople(cart[selectedProduct.id].people)
        }
    }, [selectedProduct.id])

    useEffect(() => {
        setCart(currentCart => {
            const next = { ...currentCart }
            variantList.forEach((variant) => {
                if (variant.id !== selectedProduct.id) {
                    delete next[variant.id]
                }
            })
            next[selectedProduct.id] = {
                id: selectedProduct.id,
                quantity: quantity,
                price: selectedProduct.price,
                total_price: selectedProduct.price * quantity,
                name: selectedProduct.name,
                options: selectedOptions,
                people: people
            }
            return next
        })
    }, [selectedProduct.id, selectedProduct.price, selectedProduct.name, quantity, selectedOptions, people, setCart])

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage(null)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [message])
    const isGrid = overviewType === 'grid'
    const hasOptions = selectedProduct.product_options?.length > 0
    const showOptions = quantity > 0 && hasOptions
    const options = (
        <ProductOptions
            product={selectedProduct}
            selectedOptions={selectedOptions}
            onOptionsChange={(newOptions) => {
                setSelectedOptions(newOptions)
            }}
        />
    )
    const priceBadge = (
        <div className="badge product-price-badge" style={{backgroundColor: 'var(--umr-primary, #0d6efd)', color: '#fff', fontSize: '0.875rem', width: 'fit-content'}}>
            €{selectedProduct.price} {selectedProduct.calc_pricing_type_display}
        </div>
    )
    const productImage = selectedProduct.image_object?.image ? (
        <img src={selectedProduct.image_object.image} alt={selectedProduct.name} className='img-fluid' style={{ objectFit: 'contain'}} />
    ) : null
    const variantSelectors = variantList.length > 1 ? (
        <div className="mt-2 d-flex flex-column gap-2">
            {attributeChoices.length > 0 ? attributeChoices.map((attr) => (
                <div key={attr.id}>
                    <label className="form-label small mb-1">{attr.name}</label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedAttributes[attr.id] || ''}
                        onChange={(e) => setSelectedAttributes((prev) => ({
                            ...prev,
                            [attr.id]: e.target.value || undefined,
                        }))}
                    >
                        <option value="">{i18n.t('Kies')}</option>
                        {attr.values.map((value) => (
                            <option key={value.id} value={value.id}>{value.label}</option>
                        ))}
                    </select>
                </div>
            )) : (
                <div>
                    <label className="form-label small mb-1">{i18n.t('Variant')}</label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedProduct.id}
                        onChange={(e) => {
                            setSelectedVariantId(e.target.value)
                            setSelectedOptions({})
                        }}
                    >
                        {variantList.map((variant) => (
                            <option key={variant.id} value={variant.id}>{variant.name}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    ) : null
    const quantitySelector = (
        <div className="product-quantity d-flex flex-column">
            <p className="product-quantity-label">{i18n.t('Aantal')}</p>
            <div className="btn-group" role="group" aria-label="Small button group">
                <div
                    className="py-1 btn btn-outline-light text-dark d-flex justify-content-center align-items-center"
                    onClick={() => {
                        setMessage(null);
                        setQuantity( (q) => {
                            const newQty = q===0 ? 0 : q-1
                            if (newQty === 0) {
                                setSelectedOptions({})
                            }
                            return newQty
                        })
                    }}
                    type="button" id="button-plus">
                    -
                </div>
                <input
                    data-id={selectedProduct.id}
                    name={selectedProduct.id + '_quantity'}
                    type="text"
                    min={1}
                    disabled={selectedProduct.available_listings === 0}
                    className="btn btn-quantity btn-outline-light form-control text-dark pe-0"
                    onChange={e => {
                        if(selectedProduct.available_listings === 0) {
                            return
                        }
                        setMessage(null);
                        const val = parseInt(e.target.value) || 0
                        if(val >= selectedProduct.available_listings){
                            setQuantity(selectedProduct.available_listings);
                            setMessage(<span className="alert alert-warning">{i18n.t('Je hebt het maximum beschikbare bereikt')}</span>)
                        } else {
                            setQuantity(val)
                            if (val === 0) {
                                setSelectedOptions({})
                            }
                        }
                    }}
                    value={quantity} />
                <div
                    className="py-1 btn btn-outline-light text-dark d-flex justify-content-center align-items-center"
                    type="button"
                    disabled={selectedProduct.available_listings === 0}
                    onClick={() => setQuantity( (q) => {
                        if(q===selectedProduct.available_listings){
                            setMessage(<span className="alert alert-warning">{i18n.t('Je hebt het maximum beschikbare bereikt')}</span>)
                            return q
                        }
                        return q+1
                    })}
                    id="button-minus">
                    +
                </div>
            </div>
        </div>
    )
    const availabilityBadge = selectedProduct.available_listings <= 0 ? (
        <div className="badge mt-2 umr-bg-danger" style={{backgroundColor: 'var(--umr-danger, #dc3545)', color: '#fff', width: 'fit-content'}}>
            {i18n.t('Niet beschikbaar')}
        </div>
    ) : (
        <div className="badge mt-2 umr-bg-primary" style={{backgroundColor: 'var(--umr-primary, #0d6efd)', color: '#fff', width: 'fit-content'}}>
            {i18n.t('Beschikbaar') +  ': ' + selectedProduct.available_listings}
        </div>
    )

    return (
        <>
        <div className={`card mt-1 p-3 d-flex ${isGrid ? 'flex-column product-reservation-card-grid' : 'flex-column col-12 flex-row align-items-center'}`} key={selectedProduct.id}>
            {message && <p className='position-absolute end-0 top-0 product-message mb-0 ' style={{zIndex: '100'}}>{message}</p>}
            {isGrid ? (
                <>
                    <div className="product-card-media">
                        {productImage}
                        {priceBadge}
                    </div>
                    <h5 className='product-name mb-0' style={{fontSize: '1rem'}}>
                        {selectedProduct.name}
                    </h5>
                    {selectedProduct.short_description && (
                        <p className='product-description mb-0 mt-1 p-0' style={{fontSize: '0.875rem'}}>
                            {selectedProduct.short_description}
                        </p>
                    )}
                    {variantSelectors}
                    <div className="product-card-footer">
                        {quantitySelector}
                        {availabilityBadge}
                    </div>
                </>
            ) : (
                <div className="row w-100">
                    <div className="col-4 col-lg-3">
                        {productImage}
                    </div>
                    <div className='product-information d-flex align-items-start px-1 px-md-3 col'>
                        <div className="product-information-content col d-flex flex-column h-100">
                            <h5 className='product-name mb-0 ' style={{fontSize: '1rem'}}>
                                {selectedProduct.name}
                            </h5>
                            <p className='product-description mb-0 mt-1 p-0 ' style={{fontSize: '0.875rem'}}>
                                {selectedProduct.short_description}<br></br>
                            </p>
                            {variantSelectors}
                            <div className="mt-auto">
                                {priceBadge}
                            </div>
                        </div>
                        <div className="d-flex flex-column my-auto col-4">
                            {quantitySelector}
                            {availabilityBadge}
                        </div>
                    </div>
                </div>
            )}
            {!isGrid && showOptions && options}
        </div>
        {isGrid && showOptions && (
            <div className="product-grid-options">
                {options}
            </div>
        )}
        </>
)
}

export default ProductReservationCard
